import { useState, useEffect, useRef } from "react";
import { useLocation } from "wouter";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useActiveBanners } from "@/hooks/use-cms";
import { getExpectedTopBanners, getTopBarsHint } from "@/lib/top-bars-hint";
import type { Banner as BannerType } from "@shared/schema";

// localStorage hint: remembers the total height (px) of the top banners shown
// on the last visit. Used only to refine the reserved height for returning
// visitors — visibility itself comes from the server-injected hint.
const TOP_BANNERS_HEIGHT_KEY = "uo_top_banners_height";

// Fallback per-banner height (px) when no measured height is stored yet
// (first visit): px-4 py-3 bar with one text-sm/md:text-base line.
const DEFAULT_TOP_BANNER_HEIGHT = 48;

function readDismissedBanners(): string[] {
  try {
    const stored = localStorage.getItem('dismissedBanners');
    const parsed = stored ? JSON.parse(stored) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function Banner() {
  const [location] = useLocation();
  const { data: banners, isLoading } = useActiveBanners();
  // Read synchronously so first render already knows what was dismissed.
  const [dismissedBanners, setDismissedBanners] = useState<string[]>(readDismissedBanners);
  const topContainerRef = useRef<HTMLDivElement | null>(null);
  const [measuredHeight] = useState<number>(() => {
    try {
      const raw = localStorage.getItem(TOP_BANNERS_HEIGHT_KEY);
      const parsed = raw ? parseInt(raw, 10) : 0;
      return Number.isFinite(parsed) && parsed > 0 && parsed <= 300 ? parsed : 0;
    } catch {
      return 0;
    }
  });

  // Deterministic reserved height while the banners query loads: the
  // server-injected hint (window.__UO_TOP_BARS__) tells us — on EVERY load,
  // first visit included — which top banners will render on this page.
  // A previously measured height refines the estimate when available.
  const expectedTopBanners = getExpectedTopBanners(location, dismissedBanners);
  const reservedHeight = expectedTopBanners.length > 0
    ? (measuredHeight > 0 ? measuredHeight : expectedTopBanners.length * DEFAULT_TOP_BANNER_HEIGHT)
    : (getTopBarsHint() === null && measuredHeight > 0 ? measuredHeight : 0);

  // Once banners have loaded and rendered, persist the actual height of the
  // top-banner block so the next visit can reserve the exact space up front.
  useEffect(() => {
    if (isLoading) return;
    try {
      const height = topContainerRef.current?.offsetHeight ?? 0;
      if (height > 0) {
        localStorage.setItem(TOP_BANNERS_HEIGHT_KEY, String(height));
      } else {
        localStorage.removeItem(TOP_BANNERS_HEIGHT_KEY);
      }
    } catch {
      // localStorage unavailable — skip the hint
    }
  }, [isLoading, banners, dismissedBanners, location]);

  const handleDismiss = (bannerId: string) => {
    const newDismissed = [...dismissedBanners, bannerId];
    setDismissedBanners(newDismissed);
    localStorage.setItem('dismissedBanners', JSON.stringify(newDismissed));
  };

  // Filter banners to show based on current page and dismissed status
  const activeBanners = banners?.filter((banner: BannerType) => {
    // Check if banner is dismissed (only for dismissible banners)
    if (banner.dismissible && dismissedBanners.includes(banner.id)) {
      return false;
    }

    // Check if banner is active for current date
    if (banner.startDate && new Date(banner.startDate) > new Date()) {
      return false;
    }
    if (banner.endDate && new Date(banner.endDate) < new Date()) {
      return false;
    }

    // Check if banner is for current page
    if (banner.targetPages && banner.targetPages.length > 0) {
      // Check if current location matches any target page
      const currentPath = location === '/' ? 'home' : location.slice(1);
      if (!banner.targetPages.includes(currentPath) && !banner.targetPages.includes('*')) {
        return false;
      }
    }

    return true;
  }) || [];

  // Sort by priority (higher priority first)
  const sortedBanners = activeBanners.sort((a: BannerType, b: BannerType) => (b.priority || 0) - (a.priority || 0));

  // Group banners by position
  const topBanners = sortedBanners.filter((b: BannerType) => b.position === 'top');
  const bottomBanners = sortedBanners.filter((b: BannerType) => b.position === 'bottom');
  const modalBanners = sortedBanners.filter((b: BannerType) => b.position === 'popup');

  return (
    <>
      {/* Top Banners — the wrapper keeps a reserved min-height (from the last
          visit) while the query loads, so content below doesn't jump. */}
      <div
        ref={topContainerRef}
        style={isLoading && reservedHeight > 0 ? { minHeight: `${reservedHeight}px` } : undefined}
        data-testid="container-top-banners"
      >
        {topBanners.map((banner: BannerType) => (
          <BannerBar key={banner.id} banner={banner} onDismiss={handleDismiss} />
        ))}
      </div>

      {/* Bottom Banners */}
      {bottomBanners.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-40">
          {bottomBanners.map((banner: BannerType) => (
            <BannerBar key={banner.id} banner={banner} onDismiss={handleDismiss} />
          ))}
        </div>
      )}

      {/* Modal Banners */}
      {modalBanners.map((banner: BannerType) => (
        <BannerModal key={banner.id} banner={banner} onDismiss={handleDismiss} />
      ))}
    </>
  );
}

interface BannerBarProps {
  banner: BannerType;
  onDismiss: (id: string) => void;
}

function BannerBar({ banner, onDismiss }: BannerBarProps) {
  const bgColor = banner.backgroundColor || '#D4A848';
  const textColor = banner.textColor || '#FFFFFF';

  return (
    <div 
      className="relative px-4 py-3 text-center"
      style={{
        backgroundColor: bgColor,
        color: textColor
      }}
    >
      <div className="container mx-auto max-w-7xl flex items-center justify-center gap-4">
        <div className="flex-1 flex items-center justify-center gap-4">
          <span className="text-sm md:text-base font-medium">{banner.content}</span>
          
          {banner.ctaText && banner.ctaLink && (
            <a 
              href={banner.ctaLink}
              className="inline-flex items-center gap-2 px-4 py-1.5 bg-white text-primary rounded-md font-semibold text-sm hover:bg-gray-100 transition-colors"
              style={{ color: bgColor }}
            >
              {banner.ctaText}
            </a>
          )}
        </div>
        
        {banner.dismissible && (
          <button
            onClick={() => onDismiss(banner.id)}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:opacity-70 transition-opacity"
            aria-label="Chiudi banner"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}

interface BannerModalProps {
  banner: BannerType;
  onDismiss: (id: string) => void;
}

function BannerModal({ banner, onDismiss }: BannerModalProps) {
  const [isOpen, setIsOpen] = useState(true);
  const bgColor = banner.backgroundColor || '#FFFFFF';
  const textColor = banner.textColor || '#000000';

  if (!isOpen) return null;

  const handleClose = () => {
    setIsOpen(false);
    if (banner.dismissible) {
      onDismiss(banner.id);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div 
        className="relative max-w-md w-full p-6 rounded-lg shadow-xl"
        style={{
          backgroundColor: bgColor,
          color: textColor
        }}
      >
        {banner.dismissible && (
          <button
            onClick={handleClose}
            className="absolute right-4 top-4 p-1 hover:opacity-70 transition-opacity"
            aria-label="Chiudi"
          >
            <X className="h-5 w-5" />
          </button>
        )}
        
        <div className="mt-2">
          {banner.title && (
            <h3 className="text-xl font-bold mb-3">{banner.title}</h3>
          )}
          <p className="mb-4">{banner.content}</p>
          
          {banner.ctaText && banner.ctaLink && (
            <a 
              href={banner.ctaLink}
              className="inline-block w-full text-center px-6 py-3 bg-primary text-primary-foreground rounded-md font-semibold hover:bg-accent transition-colors"
            >
              {banner.ctaText}
            </a>
          )}
          
          {!banner.ctaLink && (
            <Button
              onClick={handleClose}
              variant="default"
              className="w-full"
            >
              Chiudi
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}